// Edge-of-screen toast stack. Editorial: hairline left border + small-caps
// kicker, no rounded chips, no shadows. Auto-dismisses via the store.

import { useAgentStore } from '../stores/agent';
import { useNotificationsStore, type Toast } from '../stores/notifications';
import styles from './ToastDeck.module.css';

export function ToastDeck() {
  const toasts = useNotificationsStore((s) => s.toasts);
  const dismiss = useNotificationsStore((s) => s.dismiss);
  const loggedIn = useAgentStore((s) => s.current !== null);

  // Hide the entire deck on the login screen. Toasts from background bus
  // activity are already gated at the source, but the visual guard is cheap.
  if (!loggedIn) return null;
  if (toasts.length === 0) return null;

  return (
    <div className={styles.deck} role="status" aria-live="polite" aria-atomic="false">
      {toasts.map((t) => (
        <ToastView key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastView({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const openable = !!toast.sessionId;
  const open = () => {
    if (!toast.sessionId) return;
    // App uses HashRouter — setting the hash navigates SPA-style (fires
    // hashchange) even though the deck is mounted outside the router tree.
    window.location.hash = `/conversation/${toast.sessionId}`;
    onDismiss();
  };
  return (
    <article
      className={styles.toast}
      data-kind={toast.kind}
      data-openable={openable ? 'true' : undefined}
      onClick={openable ? open : undefined}
      role={openable ? 'button' : undefined}
      tabIndex={openable ? 0 : undefined}
      onKeyDown={
        openable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                open();
              }
            }
          : undefined
      }
    >
      <div className={styles.body}>
        <p className={styles.kicker}>{kickerOf(toast.kind)}</p>
        <p className={styles.title}>{toast.title}</p>
        {toast.body ? <p className={styles.bodyLine}>{toast.body}</p> : null}
      </div>
      <button
        type="button"
        className={styles.close}
        aria-label="Dismiss notification"
        onClick={(e) => {
          // Don't let the click bubble to the card's open handler.
          e.stopPropagation();
          onDismiss();
        }}
      >
        ×
      </button>
    </article>
  );
}

function kickerOf(kind: Toast['kind']): string {
  switch (kind) {
    case 'visitor':
      return 'New visitor';
    case 'chat':
      return 'Incoming chat';
    case 'escalate':
      return 'AI → human';
    case 'disconnect':
      return 'Disconnected';
    case 'system':
      return 'System';
  }
}
